/**
 * <usp-search>: header search field with a suggestion list directly below it.
 * Fetches Shopify predictive search (search terms, collections, products, pages)
 * while typing and renders them in a dropdown under the input.
 */
if (!customElements.get('usp-search')) {
  const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const sizedImage = (url, width) => {
    if (!url) return '';
    try {
      const u = new URL(url, location.origin);
      u.searchParams.set('width', String(width));
      return u.toString();
    } catch {
      return url;
    }
  };

  class UspSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('.usp-search__input');
      this.panel = this.querySelector('.usp-search__panel');
      this.form = this.querySelector('form');
      this.cache = new Map();
      this.timer = null;
      this.controller = null;
      this.term = '';

      const currency = this.dataset.currency || 'USD';
      this.money = new Intl.NumberFormat('en-US', { style: 'currency', currency });

      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('focus', () => {
        if (this.panel.innerHTML && this.input.value.trim()) this.open();
      });
      this.input.addEventListener('keydown', (e) => this.onKeydown(e));
      this.panel.addEventListener('keydown', (e) => this.onKeydown(e));
      this.form.addEventListener('submit', (e) => {
        if (!this.input.value.trim()) e.preventDefault();
      });
      this.addEventListener('focusout', (e) => {
        if (e.relatedTarget && !this.contains(e.relatedTarget)) this.close();
      });
      this.onDocumentClick = (e) => {
        if (!this.contains(e.target)) this.close();
      };
      document.addEventListener('pointerdown', this.onDocumentClick);
    }

    disconnectedCallback() {
      document.removeEventListener('pointerdown', this.onDocumentClick);
    }

    onInput() {
      clearTimeout(this.timer);
      const term = this.input.value.trim();
      if (!term) {
        this.term = '';
        this.close();
        this.panel.innerHTML = '';
        return;
      }
      this.timer = setTimeout(() => this.fetchResults(term), 180);
    }

    async fetchResults(term) {
      this.term = term;
      if (this.cache.has(term)) {
        this.render(term, this.cache.get(term));
        return;
      }
      this.controller?.abort();
      this.controller = new AbortController();
      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'query,collection,product,page',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
      });
      try {
        const res = await fetch(`${this.dataset.suggestUrl}?${params}`, { signal: this.controller.signal });
        if (!res.ok) throw new Error(res.status);
        const data = await res.json();
        const results = data?.resources?.results || {};
        this.cache.set(term, results);
        if (term === this.term) this.render(term, results);
      } catch (err) {
        if (err.name !== 'AbortError' && term === this.term) this.render(term, {});
      }
    }

    highlight(text, term) {
      // Like a typical autocomplete: the typed part stays regular, the completion is bold.
      const lower = text.toLowerCase();
      const at = lower.indexOf(term.toLowerCase());
      if (at === -1) return `<strong>${escapeHtml(text)}</strong>`;
      const before = text.slice(0, at);
      const match = text.slice(at, at + term.length);
      const after = text.slice(at + term.length);
      return `<strong>${escapeHtml(before)}</strong>${escapeHtml(match)}<strong>${escapeHtml(after)}</strong>`;
    }

    price(p) {
      const min = parseFloat(p.price_min ?? p.price);
      const max = parseFloat(p.price_max ?? p.price);
      if (!Number.isFinite(min) || min <= 0) return '';
      const formatted = this.money.format(min);
      return max > min ? `From ${formatted}` : formatted;
    }

    render(term, results) {
      const queries = (results.queries || []).slice(0, 4);
      const collections = (results.collections || []).slice(0, 2);
      const products = (results.products || []).slice(0, 3);
      const pages = (results.pages || []).slice(0, 3);

      let html = '';

      if (queries.length || collections.length) {
        html += '<div class="usp-search__group"><p class="usp-search__heading">Suggestions</p><ul class="usp-search__list">';
        for (const q of queries) {
          html += `<li><a class="usp-search__link usp-search__query" href="${escapeHtml(q.url)}">${this.highlight(q.text, term)}</a></li>`;
        }
        for (const c of collections) {
          html += `<li><a class="usp-search__link usp-search__collection" href="${escapeHtml(c.url)}">${escapeHtml(c.title)}</a></li>`;
        }
        html += '</ul></div>';
      }

      if (products.length || pages.length) {
        html += `<div class="usp-search__columns${pages.length && products.length ? '' : ' usp-search__columns--single'}">`;
        if (products.length) {
          html += '<div class="usp-search__group"><p class="usp-search__heading">Products</p><ul class="usp-search__products">';
          for (const p of products) {
            const img = p.featured_image?.url || p.image;
            const alt = p.featured_image?.alt || p.title;
            const price = this.price(p);
            html += `<li><a class="usp-search__link usp-search__product" href="${escapeHtml(p.url)}">
              <span class="usp-search__thumb">${img ? `<img src="${escapeHtml(sizedImage(img, 160))}" alt="${escapeHtml(alt)}" width="64" height="64" loading="lazy">` : ''}</span>
              <span class="usp-search__product-text">
                <span class="usp-search__product-title">${escapeHtml(p.title)}</span>
                ${p.vendor ? `<span class="usp-search__vendor">${escapeHtml(p.vendor)}</span>` : ''}
                ${price ? `<span class="usp-search__price">${escapeHtml(price)}</span>` : ''}
              </span>
            </a></li>`;
          }
          html += '</ul></div>';
        }
        if (pages.length) {
          html += '<div class="usp-search__group"><p class="usp-search__heading">Pages</p><ul class="usp-search__list">';
          for (const pg of pages) {
            html += `<li><a class="usp-search__link usp-search__page" href="${escapeHtml(pg.url)}">${escapeHtml(pg.title)}</a></li>`;
          }
          html += '</ul></div>';
        }
        html += '</div>';
      }

      if (!html) {
        html = `<p class="usp-search__empty">No suggestions for “${escapeHtml(term)}”. Press Enter to search the whole store.</p>`;
      }

      const allUrl = `${this.dataset.searchUrl}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`;
      html += `<a class="usp-search__link usp-search__all" href="${escapeHtml(allUrl)}">Search for “${escapeHtml(term)}” <span aria-hidden="true">→</span></a>`;

      this.panel.innerHTML = html;
      if (document.activeElement === this.input) this.open();
    }

    open() {
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');
    }

    close() {
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-open');
    }

    onKeydown(e) {
      const links = [...this.panel.querySelectorAll('.usp-search__link')];
      const index = links.indexOf(document.activeElement);
      if (e.key === 'Escape') {
        this.close();
        this.input.focus();
        return;
      }
      if (this.panel.hidden || !links.length) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        links[(index + 1) % links.length].focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (index <= 0) this.input.focus();
        else links[index - 1].focus();
      }
    }
  }

  customElements.define('usp-search', UspSearch);
}
